import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { maintenanceConfig } from '../../environments/maintenance-config';

export interface MaintenanceEvent {
  id: string;
  name: string;
  status: string;
  scheduled_for: string;
  scheduled_until: string;
  incident_updates: Array<{
    body: string;
    status: string;
    affected_components: Array<{
      name: string;
      old_status: string;
      new_status: string;
    }>;
  }>;
  components: Array<{
    name: string;
    status: string;
  }>;
  regions?: string[];
}

export interface MaintenanceResponse {
  page: {
    id: string;
    name: string;
    url: string;
    time_zone: string;
    updated_at: string;
  };
  scheduled_maintenances: MaintenanceEvent[];
}

export interface CreateMaintenanceRequest {
  name: string;
  body: string;
  scheduled_for: string;
  scheduled_until: string;
  status: string;
  impact_override?: string;
  deliver_notifications?: boolean;
  region?: string[];
}

export interface MaintenanceApiResponse {
  id: string;
  name: string;
  status: string;
  created_at: string;
  updated_at: string;
  scheduled_for: string;
  scheduled_until: string;
  incident_updates: any[];
  components: any[];
}

@Injectable({
  providedIn: 'root'
})
export class MaintenanceService {
  private readonly API_URL = maintenanceConfig.publicApiUrl;
  
  // Status page management API configuration
  private readonly STATUS_PAGE_ID = maintenanceConfig.statusPageId;
  private readonly API_KEY = maintenanceConfig.apiKey;
  private readonly MANAGEMENT_API_URL = `${maintenanceConfig.managementApiBaseUrl}/pages/${this.STATUS_PAGE_ID}`;
  
  private maintenanceSubject = new BehaviorSubject<MaintenanceEvent | null>(null);
  public maintenance$ = this.maintenanceSubject.asObservable();

  constructor(private http: HttpClient) { }

  /**
   * Fetch scheduled maintenance events from the API
   */
  getScheduledMaintenances(): Observable<MaintenanceResponse> {
    // Create headers that bypass auth interceptor
    const headers = new HttpHeaders({
      'Skip-Auth': 'true'  // Custom header to bypass auth interceptor
    });
    
    return this.http.get<MaintenanceResponse>(this.API_URL, { headers });
  }

  /**
   * Get the current active maintenance event
   */
  getCurrentMaintenance(): Observable<MaintenanceEvent | null> {
    return this.getScheduledMaintenances().pipe(
      map(response => {
        if (response.scheduled_maintenances && response.scheduled_maintenances.length > 0) {
          // Get the first scheduled maintenance event
          const maintenance = response.scheduled_maintenances[0];
          
          // Check if it's still relevant (not in the past)
          const scheduledFor = new Date(maintenance.scheduled_for);
          const scheduledUntil = new Date(maintenance.scheduled_until);
          const now = new Date();
          
          // Show if maintenance is upcoming or currently happening
          if (scheduledUntil > now && maintenance.status === 'scheduled') {
            return maintenance;
          }
        }
        return null;
      }),
      catchError(error => {
        console.error('Error fetching maintenance data:', error);
        return of(null);
      })
    );
  }

  /**
   * Format maintenance message for display
   */
  formatMaintenanceMessage(maintenance: MaintenanceEvent): string {
    if (!maintenance) return '';

    const scheduledFor = new Date(maintenance.scheduled_for);
    const scheduledUntil = new Date(maintenance.scheduled_until);
    
    // Format dates
    const dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short'
    };
    
    const startTime = scheduledFor.toLocaleDateString('en-US', dateOptions);
    const endTime = scheduledUntil.toLocaleDateString('en-US', dateOptions);
    
    // Get maintenance message from incident updates
    const maintenanceBody = maintenance.incident_updates?.[0]?.body || 'Scheduled maintenance is planned.';
    
    // Get affected regions or components
    const affectedAreas = maintenance.regions?.join(', ') || 
                         maintenance.components?.map(c => c.name).join(', ') || 
                         'system services';
    
    return `<strong>${maintenance.name}</strong>: ${maintenanceBody} Scheduled from <strong>${startTime}</strong> to <strong>${endTime}</strong>. Affected ${maintenance.regions ? 'regions' : 'services'}: ${affectedAreas}.`;
  }

  /**
   * Create a new maintenance incident via the backend API
   */
  createMaintenanceIncident(maintenanceData: CreateMaintenanceRequest): Observable<MaintenanceApiResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    // Call the backend API instead of Statuspage API directly
    const backendUrl = 'https://developer-portal-dev.gtn.tech/sbx/portal/status-page/maintenance/create';

    const body = {
      name: maintenanceData.name,
      body: maintenanceData.body,
      scheduled_for: maintenanceData.scheduled_for,
      scheduled_until: maintenanceData.scheduled_until,
      impact_override: maintenanceData.impact_override || 'minor',
      wants_twitter_update: false,
      scheduled_remind_prior: true,
      region: maintenanceData.region || []
    };

    return this.http.post<MaintenanceApiResponse>(backendUrl, body, { headers }).pipe(
      catchError(error => {
        console.error('Error creating maintenance incident:', error);
        throw error;
      })
    );
  }

  /**
   * Update an existing maintenance incident
   */
  updateMaintenanceIncident(incidentId: string, updateData: Partial<CreateMaintenanceRequest>): Observable<MaintenanceApiResponse> {
    const headers = new HttpHeaders({
      'Authorization': `OAuth ${this.API_KEY}`,
      'Content-Type': 'application/json'
    });

    const body = {
      incident: {
        ...updateData,
        incident_updates: updateData.body ? [
          {
            body: updateData.body,
            status: updateData.status || 'scheduled'
          }
        ] : undefined
      }
    };

    return this.http.put<MaintenanceApiResponse>(`${this.MANAGEMENT_API_URL}/incidents/${incidentId}`, body, { headers }).pipe(
      catchError(error => {
        console.error('Error updating maintenance incident:', error);
        throw error;
      })
    );
  }

  /**
   * Delete a maintenance incident
   */
  deleteMaintenanceIncident(incidentId: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    // Call the backend API instead of Statuspage API directly
    const backendUrl = `https://developer-portal-dev.gtn.tech/sbx/portal/status-page/incident/${incidentId}`;

    return this.http.delete(backendUrl, { headers }).pipe(
      catchError(error => {
        console.error('Error deleting maintenance incident:', error);
        throw error;
      })
    );
  }

  /**
   * Get list of components from the status page for the affected components dropdown
   */
  getComponents(): Observable<any[]> {
    const headers = new HttpHeaders({
      'Authorization': `OAuth ${this.API_KEY}`,
      'Content-Type': 'application/json'
    });

    return this.http.get<{ components: any[] }>(`${this.MANAGEMENT_API_URL}/components`, { headers }).pipe(
      map(response => response.components || []),
      catchError(error => {
        console.error('Error fetching components:', error);
        return of([]);
      })
    );
  }

  /**
   * Check for maintenance and update the subject
   */
  checkForMaintenance(): void {
    this.getCurrentMaintenance().subscribe(maintenance => {
      this.maintenanceSubject.next(maintenance);
    });
  }
} 